import { useState } from 'react';
import { XIcon as X } from '../ui/Icons';
import { useAppStore } from '@/store/useAppStore';
import { simulateFutureGrades, GRADE_ORDER } from '@gpa-scales/shared-logic/engine/scenarioModeling';
import { GradeLevel, Term, CourseType, AGSubject } from '@gpa-scales/shared-types';
import type { Course, AppSettings, ScenarioCourse, ScenarioResult } from '@gpa-scales/shared-types';
import { ScenarioResultTable } from './ScenarioResultTable';
import { Select } from 'calc-controls';

export function FutureGradesTab({ courses, settings }: { courses: Course[]; settings: AppSettings; 'data-testid'?: string }) {
  const [hypotheticals, setHypotheticals] = useState<ScenarioCourse[]>([]);
  const [result, setResult] = useState<ScenarioResult | null>(null);
  const [scenarioName, setScenarioName] = useState('');
  const saveScenario = useAppStore((s) => s.saveScenario);

  const addHypothetical = () => {
    setHypotheticals((prev) => [
      ...prev,
      {
        name: `Future Course ${prev.length + 1}`,
        gradeLevel: GradeLevel.Senior,
        term: Term.Fall,
        academicYear: '2025-2026',
        hypotheticalGrade: 'A',
        courseType: CourseType.Standard,
        credits: 1.0,
        agSubject: AGSubject.C,
      },
    ]);
  };

  const simulate = () => {
    const res = simulateFutureGrades(courses, hypotheticals, settings);
    setResult(res);
  };

  const handleSave = () => {
    if (!result) return;
    const name = scenarioName.trim() || `Scenario ${new Date().toLocaleTimeString()}`;
    saveScenario(name, result);
    setScenarioName('');
  };

  return (
    <div className="space-y-3">
      {hypotheticals.map((h, i) => (
        <div key={i} className="flex items-center gap-2 rounded border border-border p-2 text-xs">
          <input
            value={h.name}
            onChange={(e) => {
              const copy = [...hypotheticals];
              copy[i] = { ...copy[i], name: e.target.value };
              setHypotheticals(copy);
            }}
            className="flex-1 rounded border border-border px-2 py-1"
            placeholder="Course name"
            aria-label={`Course name for ${h.name}`}
          />
          <Select
            inline
            label={`Hypothetical grade for ${h.name}`}
            value={h.hypotheticalGrade}
            onChange={(v) => {
              const copy = [...hypotheticals];
              copy[i] = { ...copy[i], hypotheticalGrade: v };
              setHypotheticals(copy);
            }}
            options={GRADE_ORDER.map((g) => ({ value: g, label: g }))}
          />
          <Select
            inline
            label={`Course type for ${h.name}`}
            value={h.courseType}
            onChange={(v) => {
              const copy = [...hypotheticals];
              copy[i] = { ...copy[i], courseType: v as CourseType };
              setHypotheticals(copy);
            }}
            options={Object.values(CourseType).map((ct) => ({
              value: ct,
              label: ct.replace('_', ' ').toUpperCase(),
            }))}
          />
          <button
            onClick={() => setHypotheticals((prev) => prev.filter((_, j) => j !== i))}
            className="text-error"
            aria-label={`Remove ${h.name}`}
          >
            <X className="h-4 w-4" aria-hidden="true" />
          </button>
        </div>
      ))}
      <div className="flex gap-2">
        <button onClick={addHypothetical} data-testid="scenario-add-course" className="rounded-md bg-primary px-3 py-1.5 text-xs font-medium text-white">
          + Add Course
        </button>
        {hypotheticals.length > 0 && (
          <button onClick={simulate} data-testid="scenario-simulate" className="rounded-md bg-success px-3 py-1.5 text-xs font-medium text-white">
            Simulate
          </button>
        )}
      </div>
      {result && (
        <div className="rounded border border-border bg-[var(--color-bg-secondary)] p-3 text-xs">
          <ScenarioResultTable result={result} />
          {/* R37 — Save button */}
          <div className="mt-3 flex items-center gap-2">
            <input
              type="text"
              value={scenarioName}
              onChange={(e) => setScenarioName(e.target.value)}
              placeholder="Scenario name (optional)"
              aria-label="Scenario name"
              className="flex-1 rounded border border-border px-2 py-1 text-xs"
            />
            <button
              onClick={handleSave}
              className="rounded-md bg-primary px-3 py-1 text-xs font-medium text-white hover:bg-primary-hover"
            >
              Save
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
